import { getDatabase, ref, get, update, push } from 'firebase/database';
import { Stairwell } from '../types/stairwells';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { validateStairwellName } from '../Helpers/stairwellUtils';
import { recordActivity } from './activityApi';
import { requireAdmin } from './adminGuard';

const optionalString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.length > 0 ? value : undefined;

/**
 * Trappuppgångar at `buildings/{buildingKey}/stairwells/{stairwellKey}`.
 * Every mutation needs an admin, like unitsApi. Names are checked against the
 * building's current stairwells (read fresh); the rules only check the length.
 * Logged as `building.updated`: the activity catalogue has no stairwell entity.
 */
export class StairwellsApi {
    private db = getDatabase();

    getStairwellsPath(buildingKey: string): string {
        return `buildings/${buildingKey}/stairwells`;
    }

    getStairwellPath(buildingKey: string, stairwellKey: string): string {
        return `${this.getStairwellsPath(buildingKey)}/${stairwellKey}`;
    }

    parseStairwell(data: unknown, key: string): Stairwell | null {
        if (!data || typeof data !== 'object') return null;
        const d = data as Record<string, unknown>;
        if (typeof d.name !== 'string' || d.name.trim().length === 0) return null;
        const stairwell: Stairwell = {
            key,
            name: d.name,
            createdBy: typeof d.createdBy === 'string' ? d.createdBy : '',
            createdWhen: typeof d.createdWhen === 'string' ? d.createdWhen : '',
        };
        const updatedBy = optionalString(d.updatedBy);
        const updatedWhen = optionalString(d.updatedWhen);
        if (updatedBy) stairwell.updatedBy = updatedBy;
        if (updatedWhen) stairwell.updatedWhen = updatedWhen;
        return stairwell;
    }

    /** Parses the raw `stairwells` node of a building model; invalid entries are skipped. */
    parseStairwells(data: unknown): { [stairwellKey: string]: Stairwell } {
        const stairwells: { [stairwellKey: string]: Stairwell } = {};
        if (!data || typeof data !== 'object') return stairwells;
        for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
            const stairwell = this.parseStairwell(value, key);
            if (stairwell) {
                stairwells[key] = stairwell;
            } else {
                console.warn(`Skipping invalid stairwell ${key}`);
            }
        }
        return stairwells;
    }

    async getStairwells(buildingKey: string): Promise<{ [stairwellKey: string]: Stairwell }> {
        const snapshot = await get(ref(this.db, this.getStairwellsPath(buildingKey)));
        return this.parseStairwells(snapshot.exists() ? snapshot.val() : undefined);
    }

    async createStairwell(buildingKey: string, name: string): Promise<Stairwell> {
        const uid = await requireAdmin(this.db, 'skapa trappuppgång');
        const validName = validateStairwellName(name, await this.getStairwells(buildingKey));
        const key = push(ref(this.db, this.getStairwellsPath(buildingKey))).key;
        if (!key) {
            throw new Error('Kunde inte skapa nyckel för trappuppgången');
        }
        const node = { name: validName, createdBy: uid, createdWhen: formatDateForStorage(new Date()) };
        await update(ref(this.db), { [this.getStairwellPath(buildingKey, key)]: node });
        recordActivity('building.updated', { buildingKey, targetKey: key, label: `Trappuppgång ${validName}` });
        return { key, ...node };
    }

    async renameStairwell(buildingKey: string, stairwellKey: string, name: string): Promise<void> {
        const uid = await requireAdmin(this.db, 'ändra trappuppgång');
        const stairwells = await this.getStairwells(buildingKey);
        if (!stairwells[stairwellKey]) {
            throw new Error('Trappuppgången hittades inte');
        }
        const validName = validateStairwellName(name, stairwells, stairwellKey);
        await update(ref(this.db, this.getStairwellPath(buildingKey, stairwellKey)), {
            name: validName,
            updatedBy: uid,
            updatedWhen: formatDateForStorage(new Date()),
        });
        recordActivity('building.updated', { buildingKey, targetKey: stairwellKey, label: `Trappuppgång ${validName}` });
    }

    /** Keys of the building's units that name the stairwell, read fresh for the delete. */
    async unitKeysInStairwell(buildingKey: string, stairwellKey: string): Promise<string[]> {
        const snapshot = await get(ref(this.db, `buildings/${buildingKey}/units`));
        const units = snapshot.exists() ? (snapshot.val() as Record<string, unknown>) : {};
        return Object.entries(units)
            .filter(([, unit]) => !!unit && typeof unit === 'object' && (unit as Record<string, unknown>).stairwellKey === stairwellKey)
            .map(([unitKey]) => unitKey);
    }

    /** Removes the stairwell and clears `stairwellKey` on its units in one multi-path update. */
    async deleteStairwell(buildingKey: string, stairwellKey: string): Promise<void> {
        const uid = await requireAdmin(this.db, 'ta bort trappuppgång');
        const snapshot = await get(ref(this.db, this.getStairwellPath(buildingKey, stairwellKey)));
        const name = snapshot.exists() ? optionalString((snapshot.val() as Record<string, unknown>).name) : undefined;
        const now = formatDateForStorage(new Date());
        const updates: Record<string, unknown> = { [this.getStairwellPath(buildingKey, stairwellKey)]: null };
        for (const unitKey of await this.unitKeysInStairwell(buildingKey, stairwellKey)) {
            const unitPath = `buildings/${buildingKey}/units/${unitKey}`;
            updates[`${unitPath}/stairwellKey`] = null;
            updates[`${unitPath}/updatedBy`] = uid;
            updates[`${unitPath}/updatedWhen`] = now;
        }
        await update(ref(this.db), updates);
        recordActivity('building.updated', { buildingKey, targetKey: stairwellKey, label: name ? `Trappuppgång ${name}` : 'Trappuppgång' });
    }
}

export const stairwellsApi = new StairwellsApi();
